import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { FluentProvider } from '@fluentui/react-components';
import UrlShortener from './components/UrlShortener/UrlShortener';
import DomainAdmin from './components/DomainAdmin/DomainAdmin';
import UrlAdmin from './components/UrlAdmin/UrlAdmin';
import ThemeToggle from './components/ThemeToggle/ThemeToggle';
import { IUser } from './components/UrlShortener/IUrlShortenerProps';
import { apiRequest, loginRequest } from './auth/msalConfig';
import { parseRoles } from './auth/getAuthToken';
import { wbdDarkTheme, wbdLightTheme } from './styles/theme';
import styles from './App.module.css';

const SHORT_DOMAIN = process.env.REACT_APP_SHORT_DOMAIN ?? 'https://go.dev.wbd-us.com';

const App: React.FC = () => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [theme, setTheme] = useState<'dark' | 'light'>(() =>
    (localStorage.getItem('wurl-theme') as 'dark' | 'light') ?? 'dark'
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('wurl-theme', theme);
  }, [theme]);

  const account = accounts[0] ?? null;

  useEffect(() => {
    if (!account) { setRolesLoaded(true); return; }
    instance.acquireTokenSilent({ ...apiRequest, account })
      .then(result => setUserRoles(parseRoles(result.accessToken)))
      .catch(() => {})
      .finally(() => setRolesLoaded(true));
  }, [account, instance]);

  if (inProgress !== InteractionStatus.None || !rolesLoaded) return null;

  const user: IUser | null = isAuthenticated && account
    ? { userDetails: account.username, userRoles }
    : null;

  const isAdmin = userRoles.includes('Wurl.Admin');

  if (!isAuthenticated) {
    instance.loginRedirect(loginRequest);
    return null;
  }

  if (!user) {
    return (
      <main>
        <p>You are not authorized to use this application. Contact your administrator to request access.</p>
      </main>
    );
  }

  return (
    <BrowserRouter>
      <FluentProvider theme={theme === 'dark' ? wbdDarkTheme : wbdLightTheme}>
        <div className={styles.pageLayout}>
          <header className={styles.pageHeader}>
            <NavLink to="/" className={styles.brand}>
              <h1 className={styles.pageTitle}>wurl</h1>
              <span className={styles.pageSubtitle}>URL shortener</span>
            </NavLink>
            <div className={styles.headerRight}>
              {isAdmin && (
                <NavLink
                  to="/admin"
                  className={({ isActive }) =>
                    `${styles.adminLink}${isActive ? ` ${styles.adminLinkActive}` : ''}`
                  }
                >
                  Admin
                </NavLink>
              )}
              <ThemeToggle theme={theme} onToggle={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} />
            </div>
          </header>
          <main className={styles.pageContent}>
            <Routes>
              <Route
                path="/"
                element={<UrlShortener user={user} shortDomain={SHORT_DOMAIN} />}
              />
              <Route
                path="/admin"
                element={
                  isAdmin ? (
                    <>
                      <UrlAdmin userUpn={account.username} />
                      <DomainAdmin userUpn={account.username} />
                    </>
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </FluentProvider>
    </BrowserRouter>
  );
};

export default App;
